import React, { useEffect, useRef, useState } from 'react';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface OptionItem {
  value: string | number;
  label: string;
}

interface CompactSearchSelectProps {
  label?: string;
  name: string;
  value: string | number;
  onChange: (
    event: React.ChangeEvent<{ name: string; value: string | number }>
  ) => void;
  options: OptionItem[];
  placeholder?: string;
  searchPlaceholder?: string;
  error?: string;
  iconKey?: IconKey;
  disabled?: boolean;
  required?: boolean;
  /** Aplica mb-4. Por defecto false para filas/grillas compactas. */
  withMargin?: boolean;
  className?: string;
}

/**
 * {@link CompactSelectField} + buscador: mismo tamaño/estilo pensado para
 * popovers y filtros, pero con un input de búsqueda dentro del desplegable
 * para listas largas (categorías, clientes, etc.). Filtra localmente por
 * `label` (sin distinguir mayúsculas/acentos exactos).
 */
const CompactSearchSelect: React.FC<CompactSearchSelectProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = 'Seleccione una opción',
  searchPlaceholder = 'Buscar...',
  error,
  iconKey,
  disabled,
  required,
  withMargin = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(
    (opt) => String(opt.value) === String(value)
  );

  const normalized = (text: string) => text.toLowerCase();

  const filteredOptions = search.trim()
    ? options.filter((opt) => normalized(opt.label).includes(normalized(search)))
    : options;

  // Menú desplegable "inteligente": si no cabe abajo (y sí arriba), abre hacia arriba.
  const MENU_MAX_HEIGHT = 260;
  const toggleOpen = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        setDropUp(spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow);
      }
      return !prev;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      return;
    }
    const timeout = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timeout);
  }, [isOpen]);

  const emit = (nextValue: string | number) => {
    onChange({
      target: { name, value: nextValue },
    } as React.ChangeEvent<{ name: string; value: string | number }>);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${withMargin ? 'mb-4' : ''} ${className}`}
    >
      {label && (
        <label
          htmlFor={name}
          className="mb-2.5 flex items-center gap-1 text-sm font-medium text-black dark:text-white"
        >
          {label}
          {required && <RequiredAsteriskTooltip delay={400} />}
        </label>
      )}

      <div
        className={`
          bg-gray-1 flex h-10 items-stretch overflow-hidden rounded-lg border
          transition-all duration-200
          focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10
          dark:border-strokedark dark:bg-meta-4
          ${error ? 'border-danger' : 'border-stroke'}
          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        {iconKey && (
          <div className="flex items-center justify-center border-r border-stroke bg-gray-2 px-2.5 text-bodydark2 dark:border-strokedark dark:bg-boxdark-2">
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        <button
          type="button"
          id={name}
          disabled={disabled}
          onClick={toggleOpen}
          className={`flex flex-1 items-center truncate bg-transparent px-3 text-left text-sm outline-none ${
            disabled
              ? 'cursor-not-allowed text-bodydark2'
              : 'cursor-pointer text-black dark:text-white'
          }`}
        >
          {selectedOption ? (
            selectedOption.label
          ) : (
            <span className="text-body dark:text-bodydark2">{placeholder}</span>
          )}
        </button>

        <span
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            toggleOpen();
          }}
          className={`flex items-center pr-2.5 ${
            disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'
          }`}
        >
          <svg
            className={`fill-current transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-primary' : 'text-body dark:text-bodydark2'
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
            />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div
          className={`absolute left-0 z-[999999] w-full overflow-hidden rounded-lg border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark ${
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full border-b border-stroke bg-transparent px-3 py-2 text-sm text-black outline-none placeholder:text-bodydark2 dark:border-strokedark dark:text-white"
          />

          <ul className="max-h-52 overflow-y-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    onClick={() => emit(option.value)}
                    className={`w-full px-3 py-1.5 text-left text-sm transition hover:bg-primary hover:text-white dark:text-white ${
                      String(option.value) === String(value)
                        ? 'bg-primary/10 font-semibold text-primary'
                        : 'text-black'
                    }`}
                  >
                    {option.label}
                  </button>
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-sm text-bodydark2">
                Sin resultados
              </li>
            )}
          </ul>
        </div>
      )}

      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
};

export default CompactSearchSelect;
